import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface VeiculoFrota {
  id: string;
  modelo: string;
  placa: string;
  cor_tag: string;
  categoria: string;
  observacao: string | null;
  ativo: boolean;
}

const FALLBACK_VEICULOS: VeiculoFrota[] = [
  { id: '1', modelo: 'Fiat Toro', placa: 'RHS8E77', cor_tag: '#10B981', categoria: 'Fiat Toro', observacao: null, ativo: true },
  { id: '2', modelo: 'Fiat Toro', placa: 'SEV1I93', cor_tag: '#10B981', categoria: 'Fiat Toro', observacao: null, ativo: true },
  { id: '3', modelo: 'Renault Kwid', placa: 'SEJ3B08', cor_tag: '#F59E0B', categoria: 'Renault Kwid', observacao: null, ativo: true },
  { id: '4', modelo: 'VW Saveiro', placa: 'SFG5H12', cor_tag: '#EC4899', categoria: 'VW Saveiro', observacao: null, ativo: true },
  { id: '5', modelo: 'Jeep Compass', placa: 'SFN8E53', cor_tag: '#EF4444', categoria: 'Jeep Compass', observacao: 'Emergencial', ativo: true },
];

export function useVeiculosFrota() {
  const [veiculos, setVeiculos] = useState<VeiculoFrota[]>(FALLBACK_VEICULOS);
  const [loading, setLoading] = useState(true);
  const [usingDatabase, setUsingDatabase] = useState(false);

  const loadVeiculos = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('frota_veiculos')
        .select('*')
        .eq('ativo', true)
        .order('modelo', { ascending: true });

      if (!error && data && data.length > 0) {
        setVeiculos(data);
        setUsingDatabase(true);
      } else {
        setVeiculos(FALLBACK_VEICULOS);
        setUsingDatabase(false);
      }
    } catch {
      setVeiculos(FALLBACK_VEICULOS);
      setUsingDatabase(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVeiculos();
  }, [loadVeiculos]);

  const addVeiculo = async (veiculo: { modelo: string; placa: string; cor_tag: string; categoria: string; observacao?: string }) => {
    const { data, error } = await supabase
      .from('frota_veiculos')
      .insert({
        modelo: veiculo.modelo,
        placa: veiculo.placa.toUpperCase(),
        cor_tag: veiculo.cor_tag,
        categoria: veiculo.categoria || veiculo.modelo,
        observacao: veiculo.observacao || null,
      })
      .select()
      .maybeSingle();

    if (error) throw error;
    await loadVeiculos();
    return data;
  };

  const removeVeiculo = async (id: string) => {
    const { error } = await supabase
      .from('frota_veiculos')
      .update({ ativo: false })
      .eq('id', id);

    if (error) throw error;
    await loadVeiculos();
  };

  const deleteVeiculo = async (id: string) => {
    const { error } = await supabase
      .from('frota_veiculos')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await loadVeiculos();
  };

  return { veiculos, loading, usingDatabase, loadVeiculos, addVeiculo, removeVeiculo, deleteVeiculo };
}

export function getVeiculoLabel(v: VeiculoFrota): string {
  const obs = v.observacao ? ` (${v.observacao})` : '';
  return `${v.modelo} - ${v.placa}${obs}`;
}
